import React, { useEffect } from 'react';
import { useImmer } from 'use-immer';
import { useGate, useStore } from 'effector-react';
import useConstant from 'use-constant';
import { Collapse } from '@components/Collapse';
import Slider from '@components/Slider';
import { $loadedScene } from '@stores/loadedStore';
import { MainPlayer } from '@stores/krpPlayerStores';
import { $sceneMapApi } from '@stores/dataStores';
import { $GateRotateItemEditing } from '@stores/autoRotateStores';
import PanelBase from '../../panel/PanelBase';
import { deleteRotateItem } from '../actions/krpRotate';

interface Props {
  id: string;
  onRequestClose: () => void;
}

export function PanelRotateItemPano({ id, onRequestClose }: Props) {
  const scene = useStore($loadedScene)!;
  const list = scene.config.autoRotate?.tourData ?? [];
  const index = list.findIndex((x) => x.id === id);
  const defaultData = useConstant(() => list[index]);
  const [data, setData] = useImmer(defaultData);
  const isChanged = defaultData.fov !== data.fov;
  useGate($GateRotateItemEditing, { isChanged, id });

  const setFov = (value: number) => {
    const val = Math.round(value * 10) / 10;
    setData((draft) => {
      draft.fov = val;
    });
    MainPlayer.get()?.updateView({ fov: val });
  };

  useEffect(() => {
    MainPlayer.get()?.lookTo({
      fov: defaultData.fov,
      hlookat: defaultData.hlookat,
      vlookat: defaultData.vlookat,
    });
    MainPlayer.get()?.updateTextHotspot({
      id: defaultData.id,
      font: { fontColor: '#ffffff' },
      background: { bgColor: '#FA6400' },
    });
    if (defaultData.fov < scene.view.fovMin) {
      setFov(scene.view.fovMin);
    } else if (defaultData.fov > scene.view.fovMax) {
      setFov(scene.view.fovMax);
    }
    return () => {
      MainPlayer.get()?.updateTextHotspot({
        id: defaultData.id,
        font: { fontColor: '#1a1a1a' },
        background: { bgColor: '#ffffff' },
      });
    };
  }, []);

  const handleConfirm = () => {
    $sceneMapApi.updateScene({
      id: scene.id,
      updater: (draft) => {
        const tourData = draft.config.autoRotate?.tourData ?? [];
        tourData[index] = data;
      },
    });
  };

  return (
    <PanelBase
      title="导览节点设置"
      isChanged={isChanged}
      onConfirm={handleConfirm}
      onRequestClose={onRequestClose}
      onDeleteClick={() => deleteRotateItem({ id })}
    >
      <div className="mb-10 flex h-50 items-center rounded bg-black-#28 px-10">
        {`节点序号：${index + 1}`}
      </div>
      <Collapse title="节点FOV设置">
        <Slider
          min={scene.view.fovMin}
          max={scene.view.fovMax}
          value={data.fov}
          valToString={(val) => String(Math.round(val * 10) / 10)}
          onChange={setFov}
        >
          <Slider.Header title="FOV显示范围" />
          <Slider.Bar step={0.1} />
          <Slider.Footer />
        </Slider>
      </Collapse>
    </PanelBase>
  );
}
